import { type Infer, v } from 'convex/values'

// Form-template enums, the single source of truth shared between the schema,
// the feature functions, the seed loader, and the catalog UI. Mirrors the
// `taskEnums.ts` pattern.

/** Whether a template is a Planno-authored seed or a workspace-owned template. */
export const formTemplateScope = v.union(v.literal('planno'), v.literal('workspace'))

/** Top-split of the catalog: briefings live at project start, checklists during construction. */
export const formTemplateCategory = v.union(v.literal('briefing'), v.literal('checklist'))

/** Briefing sub-taxonomy — project archetype the briefing is for. */
export const formTemplateArchetype = v.union(
  v.literal('residential'),
  v.literal('commercial'),
  v.literal('specialized')
)

/** Checklist sub-taxonomy — construction phase the checklist applies to. */
export const formTemplatePhase = v.union(
  v.literal('foundation'),
  v.literal('structure'),
  v.literal('masonry'),
  v.literal('finishing'),
  v.literal('handover')
)

/** Checklist secondary sub-taxonomy — trade the checklist applies to. Optional. */
export const formTemplateTrade = v.union(
  v.literal('electrical'),
  v.literal('plumbing'),
  v.literal('structural')
)

/**
 * The field types a template item can use. Authoring intent is preserved by
 * splitting `shortText` and `longText`; per Key Technical Decisions this is
 * easier to merge later than to split.
 *
 * `conformity` is the inspection primitive and is offered only on checklist
 * templates. A checkbox cannot express a vistoria item: "não marcado" collapses
 * "está errado" and "não se aplica" into the same silence, and those two lead
 * opposite places. The tri-state is also the hinge for pendências — a
 * `non_compliant` answer is what triggers the "Criar pendência" nudge.
 */
export const formFieldType = v.union(
  v.literal('checkbox'),
  v.literal('conformity'),
  v.literal('shortText'),
  v.literal('longText'),
  v.literal('number'),
  v.literal('photo'),
  v.literal('photoMulti'),
  v.literal('enum'),
  v.literal('ratingScale'),
  v.literal('date'),
  v.literal('contactPick')
)

/**
 * The three states a `conformity` answer can hold. Stored as the envelope's
 * `value`. pt-BR surface labels are Conforme / Não conforme / N/A.
 */
export const CONFORMITY_VALUES = ['compliant', 'non_compliant', 'not_applicable'] as const
export type ConformityValue = (typeof CONFORMITY_VALUES)[number]

export type FormTemplateScope = Infer<typeof formTemplateScope>
export type FormTemplateCategory = Infer<typeof formTemplateCategory>
export type FormTemplateArchetype = Infer<typeof formTemplateArchetype>
export type FormTemplatePhase = Infer<typeof formTemplatePhase>
export type FormTemplateTrade = Infer<typeof formTemplateTrade>
export type FormFieldType = Infer<typeof formFieldType>

export const FORM_TEMPLATE_SCOPES: FormTemplateScope[] = formTemplateScope.members.map(
  (m) => m.value
)
export const FORM_TEMPLATE_CATEGORIES: FormTemplateCategory[] = formTemplateCategory.members.map(
  (m) => m.value
)
export const FORM_TEMPLATE_ARCHETYPES: FormTemplateArchetype[] = formTemplateArchetype.members.map(
  (m) => m.value
)
export const FORM_TEMPLATE_PHASES: FormTemplatePhase[] = formTemplatePhase.members.map(
  (m) => m.value
)
export const FORM_TEMPLATE_TRADES: FormTemplateTrade[] = formTemplateTrade.members.map(
  (m) => m.value
)
export const FIELD_TYPES: FormFieldType[] = formFieldType.members.map((m) => m.value)

/**
 * Field types only a checklist template may author. `validateSectionsPayload`
 * has no view of the parent template, so the gate lives with the caller that
 * does — `formTemplates.updateSections` — and the builder hides them from the
 * picker on a briefing.
 */
export const CHECKLIST_ONLY_FIELD_TYPES: FormFieldType[] = ['conformity']

/** The field types offered for a template of the given category. */
export const fieldTypesFor = (category: FormTemplateCategory): FormFieldType[] =>
  category === 'checklist'
    ? FIELD_TYPES
    : FIELD_TYPES.filter((type) => !CHECKLIST_ONLY_FIELD_TYPES.includes(type))

/** The rating-scale presets a `ratingScale` item can pick. */
export const RATING_SCALE_PRESETS = ['bad-regular-good-excellent', '1-5'] as const
export type RatingScalePreset = (typeof RATING_SCALE_PRESETS)[number]

/** The two contact kinds a `contactPick` item can filter to. */
export const CONTACT_PICK_KINDS = ['person', 'company'] as const
export type ContactPickKind = (typeof CONTACT_PICK_KINDS)[number]
